import { useState, useEffect } from 'react';
import { useNavigate }         from 'react-router-dom';
import { getSessions }         from '../api/pos.api.js';
import { IcoClipboard, IcoUser, IcoReceipt, IcoSearch } from '../components/common/Icons.jsx';
import styles from './SessionsHistoryScreen.module.css';

const fmt     = (n) => `$${Number(n ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
const fmtDate = (iso) => new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });

const STATUS_LABEL = { open: 'Abierta', closed: 'Cerrada' };

export default function SessionsHistoryScreen() {
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [search,   setSearch]   = useState('');

  useEffect(() => {
    getSessions({ limit: 50 })
      .then((data) => setSessions(Array.isArray(data) ? data : []))
      .catch(() => setError('No se pudo cargar el historial de sesiones'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setExpanded((p) => (p === id ? null : id));

  const q = search.trim().toLowerCase();
  const filtered = q
    ? sessions.filter((s) =>
        (s.terminal_name ?? '').toLowerCase().includes(q) ||
        (s.cashier_name  ?? '').toLowerCase().includes(q) ||
        (s.opened_at ? new Date(s.opened_at).toLocaleDateString('es-MX') : '').includes(q)
      )
    : sessions;

  return (
    <div className={styles.root}>
      <div className={styles.inner}>

        <div className={styles.header}>
          <button className="btn btn--ghost" onClick={() => navigate(-1)}>← Volver</button>
          <h1 className={styles.title}>Sesiones anteriores</h1>
        </div>

        <div className={styles.searchRow}>
          <IcoSearch size={15} style={{ color: 'var(--fg-muted)', flexShrink: 0 }} />
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Terminal, cajero o fecha…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div className={styles.centered}><span className="spinner" /></div>
        ) : error ? (
          <p className={styles.error}>{error}</p>
        ) : sessions.length === 0 ? (
          <div className={styles.empty}>
            <p className={styles.emptyIcon}><IcoClipboard size={40} /></p>
            <p className={styles.emptyText}>Sin sesiones registradas</p>
          </div>
        ) : (
          <div className={styles.list}>
            {filtered.length === 0 && (
              <p className={styles.error} style={{ textAlign: 'center', marginTop: 24 }}>Sin resultados para "{search}"</p>
            )}
            {filtered.map((s) => {
              const isOpen = expanded === s._id;
              const isCurrent = s.status === 'open';
              return (
                <div
                  key={s._id}
                  className={`${styles.rowWrap} ${isCurrent ? styles.rowCurrent : ''}`}
                >
                  <div className={styles.row} onClick={() => toggle(s._id)}>
                    <div className={styles.rowLeft}>
                      <div className={styles.rowInfo}>
                        <div className={styles.rowTop}>
                          <span className={styles.rowTerminal}>{s.terminal?.name ?? 'Terminal'}</span>
                          <span className={`${styles.statusBadge} ${isCurrent ? styles.statusOpen : styles.statusClosed}`}>
                            {STATUS_LABEL[s.status] ?? s.status}
                          </span>
                        </div>
                        <span className={styles.rowDate}>{fmtDate(s.opened_at ?? s.createdAt)}</span>
                        {s.cashier_name && (
                          <span className={styles.rowCashier}><IcoUser size={12} /> {s.cashier_name}</span>
                        )}
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <strong className={styles.rowTotal}>{fmt(s.sales_total)}</strong>
                      <span className={styles.rowCount}>{s.sales_count ?? 0} venta{(s.sales_count ?? 0) !== 1 ? 's' : ''}</span>
                    </div>
                  </div>

                  {isOpen && (
                    <div className={styles.rowDetail}>
                      <div className={styles.detailGrid}>
                        <div className={styles.detailItem}>
                          <span className={styles.detailLabel}>Efectivo esperado</span>
                          <strong className={styles.detailVal}>{fmt(s.expected_cash)}</strong>
                        </div>
                        {s.opening_cash != null && (
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Fondo inicial</span>
                            <strong className={styles.detailVal}>{fmt(s.opening_cash)}</strong>
                          </div>
                        )}
                        {s.closing_cash != null && (
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Efectivo contado</span>
                            <strong className={styles.detailVal}>{fmt(s.closing_cash)}</strong>
                          </div>
                        )}
                        {s.closed_at && (
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Cierre</span>
                            <strong className={styles.detailVal}>{fmtDate(s.closed_at)}</strong>
                          </div>
                        )}
                      </div>
                      {!isCurrent && (
                        <button
                          className="btn btn--ghost"
                          style={{ marginTop: 8, width: '100%', fontSize: 13 }}
                          onClick={() => navigate('/sales-history', { state: { sessionId: s._id } })}
                        >
                          <IcoReceipt size={13} /> Ver ventas de esta sesión
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
